// =============================================================================
// PaymentFields — shared payment capture (record payment + order deposit)
// =============================================================================
// Method tiles, a big amount field with quick-fill buttons, a reference that's
// required for traceable methods (Juice / bank transfer / card / cheque), an
// optional delivery-COD toggle, and an "after this payment" preview.

import type { PaymentMethod } from '@kalachand/shared';
import { METHODS_REQUIRING_REFERENCE } from '@kalachand/shared';
import { Money } from './Money';
import { Input } from './Input';
import { cn } from '../lib/cn';

export const METHOD_TILES: { key: PaymentMethod; label: string }[] = [
  { key: 'cash', label: 'Cash' },
  { key: 'juice', label: 'MCB Juice' },
  { key: 'emtel', label: 'Emtel Money' },
  { key: 'myt', label: 'MYT Money' },
  { key: 'bank_transfer', label: 'Bank xfer' },
  { key: 'card', label: 'Card' },
  { key: 'cheque', label: 'Cheque' },
  { key: 'other', label: 'Other' },
];

export interface PaymentDraft {
  method: PaymentMethod;
  amount: string;
  reference: string;
}

export const emptyPayment = (amount = ''): PaymentDraft => ({
  method: 'cash',
  amount,
  reference: '',
});

export const paymentNeedsRef = (m: PaymentMethod): boolean =>
  METHODS_REQUIRING_REFERENCE.includes(m);

// Method-specific wording for the mandatory reference field.
const REF_FIELD: Partial<Record<PaymentMethod, { label: string; placeholder: string }>> = {
  cheque: { label: 'Cheque number', placeholder: 'Number printed on the cheque' },
  card: { label: 'Transaction / receipt #', placeholder: 'Terminal receipt number' },
  juice: { label: 'Juice transaction ref', placeholder: 'MCB Juice reference' },
  emtel: { label: 'Emtel Money ref', placeholder: 'Emtel Money reference' },
  myt: { label: 'MYT Money ref', placeholder: 'my.t money reference' },
  bank_transfer: { label: 'Transfer reference', placeholder: 'Bank transfer reference' },
};

/** Has a positive amount and (where required) a reference. */
export function paymentValid(d: PaymentDraft): boolean {
  const amt = Number(d.amount);
  if (!(amt > 0)) return false;
  if (paymentNeedsRef(d.method) && !d.reference.trim()) return false;
  return true;
}

export function PaymentFields({
  draft,
  onChange,
  balance,
  userName,
  shopName,
  canRecordCheque = false,
  depositOption,
  onMixed,
}: {
  draft: PaymentDraft;
  onChange: (d: PaymentDraft) => void;
  balance: number;
  userName?: string;
  shopName?: string;
  /** When false, the Cheque tile is hidden (needs `payments.record_cheque`). */
  canRecordCheque?: boolean;
  /** Optional extra quick-fill (e.g. the minimum deposit) shown first. */
  depositOption?: { label: string; value: number };
  /** When set, a "Mixed payment" tile is shown alongside the methods; clicking
   *  it switches to multi-method (split-tender) entry. */
  onMixed?: () => void;
}): JSX.Element {
  const set = (patch: Partial<PaymentDraft>): void => onChange({ ...draft, ...patch });
  const amt = Number(draft.amount) || 0;
  const after = Math.max(0, balance - amt);
  const needsRef = paymentNeedsRef(draft.method);
  const visibleTiles = canRecordCheque
    ? METHOD_TILES
    : METHOD_TILES.filter((t) => t.key !== 'cheque');

  const quick: { label: string; value: number }[] = [
    ...(depositOption && depositOption.value > 0 ? [depositOption] : []),
    { label: 'Half', value: Math.round((balance / 2) * 100) / 100 },
    { label: 'Balance', value: balance },
    { label: 'Round', value: Math.floor(balance / 100) * 100 },
  ].filter((q) => q.value > 0);

  return (
    <div className="space-y-4">
      {/* Method */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium mb-1.5">
          Method
        </div>
        <div className="grid grid-cols-3 gap-2">
          {visibleTiles.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => set({ method: m.key })}
              className={cn(
                'px-3 py-2 rounded border text-sm transition',
                draft.method === m.key
                  ? 'bg-accent text-white border-accent'
                  : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
              )}
            >
              {m.label}
            </button>
          ))}
          {onMixed && (
            <button
              type="button"
              onClick={onMixed}
              className="px-3 py-2 rounded border border-dashed border-rule bg-paper-2 text-sm text-ink-2 transition hover:border-accent/40 hover:text-accent"
            >
              Mixed payment
            </button>
          )}
        </div>
      </div>

      {/* Amount */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium mb-1.5">
          Amount
        </div>
        <div className="flex items-center gap-2 rounded-md border border-rule bg-surface px-3 py-2 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-soft">
          <span className="text-ink-3 text-lg">Rs</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.amount}
            onChange={(e) => set({ amount: e.target.value })}
            className="flex-1 min-w-0 text-2xl kc-mono kc-tnum bg-transparent text-ink focus:outline-none"
            placeholder="0"
          />
        </div>
        {quick.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {quick.map((q) => {
              const active = amt === q.value;
              return (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => set({ amount: String(q.value) })}
                  className={cn(
                    'px-2.5 py-1 rounded text-xs border transition kc-tnum',
                    active
                      ? 'bg-accent-soft border-accent text-accent'
                      : 'bg-surface border-rule text-ink-2 hover:border-accent/40',
                  )}
                >
                  {q.label} · {q.value.toLocaleString()}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Reference — required for traceable methods */}
      {needsRef && (
        <Input
          label={REF_FIELD[draft.method]?.label ?? 'Reference / receipt #'}
          required
          value={draft.reference}
          onChange={(e) => set({ reference: e.target.value })}
          placeholder={REF_FIELD[draft.method]?.placeholder ?? 'Juice ref, card receipt, cheque #…'}
          hint="Required for this payment method."
        />
      )}

      {/* Received by / at shop (read-only context) */}
      {(userName || shopName) && (
        <div className="grid grid-cols-2 gap-3">
          {userName && <ReadField label="Received by" value={userName} />}
          {shopName && <ReadField label="At shop" value={shopName} />}
        </div>
      )}

      {/* After this payment */}
      {amt > 0 && (
        <div
          className={cn(
            'rounded-md px-3 py-2 text-sm flex items-center justify-between',
            after === 0 ? 'bg-positive-soft text-positive' : 'bg-paper-2 text-ink-2',
          )}
        >
          <span className="text-[10px] uppercase tracking-wider font-medium">
            After this payment
          </span>
          <span>
            <Money value={String(after)} className="font-medium" />{' '}
            {after === 0 ? '· Paid in full' : 'remaining'}
          </span>
        </div>
      )}
    </div>
  );
}

function ReadField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-rule bg-paper-2/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div className="text-sm text-ink mt-0.5 truncate">{value}</div>
    </div>
  );
}
